import { useEffect, useState, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Layout from './components/Layout';
import HomePage from './pages/HomePage';
import GalleryPage from './pages/GalleryPage';
import ImportPage from './pages/ImportPage';
import EditorPage from './pages/EditorPage';
import ConnectPage from './pages/ConnectPage';
import DashboardPage from './pages/DashboardPage';
import SecurityPage from './pages/SecurityPage';
import LoginPage from './pages/LoginPage';
import AdminPage from './pages/AdminPage';
import Toast from './components/Toast';
import { auth, authService, firestoreService, isFirebaseConfigured } from './services/firebase';
import { useAppStore } from './stores/appStore';

function DashboardAccess({ ready, admin = false, children }: { ready: boolean; admin?: boolean; children: ReactNode }) {
  const { user } = useAppStore();
  const location = useLocation();
  if (!ready) return <div role="status" className="p-8 text-center">Mengesahkan akaun...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (admin && (user.role !== 'admin' || !isFirebaseConfigured || location.state?.fromDashboard !== true)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

export default function App() {
  const { user, setUser, setMySites } = useAppStore();
  const [authReady, setAuthReady] = useState(!isFirebaseConfigured);

  useEffect(() => {
    if (!isFirebaseConfigured) return;

    let generation = 0;
    const unsubscribe = authService.onAuthChange(async (fbUser) => {
      const current = ++generation;
      setAuthReady(false);
      setUser(null);
      setMySites([]);
      if (fbUser) {
        try {
          const profile = await firestoreService.createOrGetUserProfile(fbUser);
          if (current !== generation || auth?.currentUser?.uid !== fbUser.uid) return;
          setUser(profile);
          setAuthReady(true);
          const sites = await firestoreService.getUserSites(fbUser.uid);
          if (current !== generation || auth?.currentUser?.uid !== fbUser.uid) return;
          setMySites(sites);
        } catch (err) {
          console.warn('Failed to load user profile:', err);
          if (current === generation) {
            useAppStore.getState().showToast(
              useAppStore.getState().user
                ? 'Login berjaya, tetapi senarai laman gagal dimuatkan. Muat semula untuk mencuba lagi.'
                : 'Profil akaun gagal dimuatkan. Sila cuba log masuk semula.',
              'error'
            );
          }
        } finally {
          if (current === generation) setAuthReady(true);
        }
      } else {
        setUser(null);
        setMySites([]);
        setAuthReady(true);
      }
    });

    return () => { generation++; unsubscribe(); };
  }, [setUser, setMySites]);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={authReady && user ? <Navigate to="/dashboard" replace /> : <><LoginPage /><Toast /></>} />
        <Route path="/" element={<Layout><HomePage /></Layout>} />
        <Route path="/gallery" element={<Layout><GalleryPage /></Layout>} />
        <Route path="/import" element={<Layout><ImportPage /></Layout>} />
        <Route path="/editor" element={<Layout showNavbar={false}><EditorPage /></Layout>} />
        <Route path="/connect" element={<Layout><ConnectPage /></Layout>} />
        <Route path="/dashboard" element={<DashboardAccess ready={authReady}><Layout><DashboardPage /></Layout></DashboardAccess>} />
        <Route path="/security" element={<Layout><SecurityPage /></Layout>} />
        <Route path="/admin" element={<DashboardAccess ready={authReady} admin><Layout><AdminPage /></Layout></DashboardAccess>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
